/**
 * Workspace context — the single source of truth for which modules are
 * enabled on the current clinic. Sidebar, Module Manager and Settings
 * consume this hook.
 *
 * We fetch once on mount, cache in state, and expose:
 *   - catalog: full module list from the backend
 *   - enabled: Set of module ids that are currently ON
 *   - isEnabled(id): boolean
 *   - refresh(): re-fetch after a toggle
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const WorkspaceContext = createContext(null);

// Static fallback used on first paint — before /workspace/modules resolves.
// Mirrors the backend catalog defaults so the sidebar never flashes empty.
const DEFAULT_ENABLED = new Set([
  "dashboard", "patients", "calendar", "treatments", "billing", "settings",
  "waiting_room", "inventory", "whatsapp", "analytics",
]);

export function WorkspaceProvider({ children }) {
  const [catalog, setCatalog] = useState([]);
  const [enabled, setEnabled] = useState(DEFAULT_ENABLED);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/workspace/modules");
      setCatalog(data.catalog || []);
      setEnabled(new Set(data.enabled_modules || []));
      setCanEdit(!!data.can_edit);
    } catch { /* not signed in yet or 401 — sidebar falls back to core */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({
    catalog, enabled, canEdit, loading, refresh,
    isEnabled: (id) => enabled.has(id),
  }), [catalog, enabled, canEdit, loading, refresh]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be inside <WorkspaceProvider>");
  return ctx;
}
